import { DEFAULT_LANGUAGE } from '@nykaa/intl-shared/constants';
import {
  PLP_SORTING,
  CLICK,
  NO_OFFER_MESSAGE,
  NO_OFFER_ID,
  BEST_SELLER_PAGE,
} from 'shared/constants';
import { pushData, pushEvent } from 'helpers/utils/dataLayer';
import { coverageCalc, plpFiltersAndSortingToString } from '.';
import { OfferData } from 'types/productCard';
import { isShade } from 'Nps/shared/helpers';
import { isDeviceNotWeb } from '../device';
import { Device } from 'types';
import { webViewCallbacks } from '@nykaa/webview-wizard/constant';
import { mixpanelEventsForApp } from 'constants/trackingSources';
import { webViewCallbackhandler } from '@nykaa/webview-wizard';

interface PDOptions {
  eventName: string;
  isExperimentEnabled: boolean;
  product: any;
  clickedFrom?: string;
  lang: string;
  action: string;
  offerMessage?: string;
  offerId?: string;
  showOffer?: boolean;
  pdtTags?: string;
  dynamicTags?: string;
  clickInteraction?: string;
  position: number;
  categoryId?: string | undefined;
  categoryName?: string | undefined;
  productTags?: string[];
  brandId?: string | undefined;
  brandName?: string | undefined;
  pageType: string;
  pageName: string;
  trackingData?: string;
  interactionLocation?: string;
  device: Device;
  appTrackingKeys?: any;
  isLoggedIn?: boolean;
  isConvSearch?: boolean;
  gptConversationId?: string;
  plpPosition?: number;
  plpInteractionValue?: string;
  interactionInfo?: { clickCount?: number };
}

export const productCardTrackingData = (
  clickedFrom: string,
  lang: string,
  action: string
) => ({
  productCardActionDetails: `clickedFrom: ${clickedFrom}|lang: ${lang}|action:${action}`,
});

export const productCardAction = (options: PDOptions) => {
  const {
    eventName = '',
    isExperimentEnabled = false,
    product = {},
    clickedFrom = '',
    lang = DEFAULT_LANGUAGE,
    action = CLICK,
    offerMessage = NO_OFFER_MESSAGE,
    offerId = NO_OFFER_ID,
    showOffer = false,
    pdtTags = '',
    dynamicTags = '',
    clickInteraction = '',
    position,
    categoryId = '',
    categoryName = '',
    brandId = '',
    brandName = '',
    productTags = [],
    pageType,
    pageName,
    trackingData,
    interactionLocation,
    device,
    appTrackingKeys,
    isLoggedIn,
    isConvSearch,
    gptConversationId,
    plpPosition,
    plpInteractionValue,
  } = options;
  let productCardActionDetails = {};
  if (isExperimentEnabled && clickedFrom) {
    productCardActionDetails = productCardTrackingData(
      clickedFrom,
      lang,
      action
    );
  }
  let cardClickAction = '';
  if (clickedFrom === BEST_SELLER_PAGE) {
    cardClickAction = isShade(product.variantType) ? 'shadePage' : 'selectSize';
  }
  const offerData: OfferData = {};
  if (showOffer) {
    offerData.offerMessage = offerMessage || NO_OFFER_MESSAGE;
    offerData.offerId = offerId || NO_OFFER_ID;
  }

  const productTracked = {
    ...product,
    position,
    categoryId,
    categoryName,
    productTags,
    brandId,
    brandName,
    pageType,
    pageName,
  };

  const {
    search_term,
    interaction_location,
    nykaa_store,
    source,
    ui_design_version,
    active_tab,
  } = appTrackingKeys || {};

  if (isDeviceNotWeb(device)) {
    const {
      brandName,
      primaryCategories,
      mrp,
      variantType,
      id,
      name,
      inStock,
      productTags,
      type,
      childId,
      quantity,
    } = productTracked || {};
    const clickCountVal = window.datalayer?.plpInteractionData?.clickCount || 0;
    const variantSelectDataForApps = {
      brand_name: brandName || '',
      category_l1_name: primaryCategories?.l1?.name,
      category_l2_name: primaryCategories?.l2?.name,
      category_l3_name: primaryCategories?.l3?.name,
      discounted_percent: productTracked?.discount,
      discounted_price: productTracked?.price,
      interaction_location: interaction_location || '',
      is_logged_in: !!isLoggedIn,
      listing_type: 'searchListingPage',
      listing_value: search_term || '',
      mrp_price: mrp,
      currency: 'INR',
      nykaa_store: nykaa_store || '',
      plp_click_position: plpPosition,
      plp_interaction_value: plpInteractionValue || '',
      preview_click_location: 'PLP',
      preview_type: variantType,
      product_id: id,
      product_name: name,
      product_stock_status: inStock ? 'in Stock' : '',
      product_tags: productTags,
      product_type: type,
      source_platform: source || '',
      variant_product_id: childId,
      variant_stock_count: quantity,
      variant_type: type,
      has_nykaa_gpt_interaction: isConvSearch,
      gpt_conversation_id: gptConversationId,
      plp_click_count: clickCountVal,
      ...(ui_design_version && { ui_design_version }),
      ...(active_tab && { active_tab }),
    };

    const appParams = {
      device,
      callbackFunctionName: webViewCallbacks.doMixpanel,
      callbackParams: {
        eventName: mixpanelEventsForApp.preview_clicked,
        params: JSON.stringify(variantSelectDataForApps),
      },
    };
    webViewCallbackhandler(appParams);
  }

  pushEvent(eventName, {
    ...offerData,
    clickedProduct: { ...productTracked, trackingData },
    ...productCardActionDetails,
    pdtTags,
    dynamicTags,
    clickedFrom,
    position,
    ...(clickInteraction && { clickInteraction }),
    ...(interactionLocation && { interaction_location: interactionLocation }),
    cardClickAction,
  });
};

export const sortingCoverageCalc = (langs: string[]) => {
  let details: { [key: string]: number } = {};
  langs.forEach((lang) => {
    if (details[lang]) {
      details[lang]++;
    } else {
      details[lang] = 1;
    }
  });
  const { coverage: filtersRes = {}, sum = 0 } = coverageCalc(details);
  const strFromObj = plpFiltersAndSortingToString(filtersRes, sum, 'sorting_');
  pushData({ [PLP_SORTING]: strFromObj });
};

export const filtersCoverageCalc = (filters: any) => {
  let details: { [key: string]: number } = {};
  filters?.forEach((filter) => {
    const { contentLang } = filter;
    if (contentLang) {
      details[contentLang] = details[contentLang]
        ? (details[contentLang] += 1)
        : 1;
    }
  });
  if (Object.keys(details).length) {
    const { coverage: filtersRes = {}, sum = 0 } = coverageCalc(details);
    const strFromObj = plpFiltersAndSortingToString(
      filtersRes,
      sum,
      'filters_'
    );
    pushData({ plpFilters: strFromObj });
  }
};
